import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";

/**
 * Shared UI primitives for the two Meyar product pages.
 * Both /meyar/cyber-security-grc/ and /meyar/enterprise-grc/ import from
 * here so palette, buttons, diagram and accordion stay identical.
 */

export const ACCENT = "#58AF58";        // Wafa primary green
export const ACCENT_DEEP = "#0E4F3A";   // Wafa deep green
export const DARK_BG = "#0E4F3A";
export const TINT_BG = "#F5F8FA";

export function FilledButton({ children, to = "/contact", className = "", testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`inline-flex items-center justify-center gap-2 rounded-md bg-wafa-primary px-5 py-3 text-sm font-semibold text-white shadow-sm transition-all hover:bg-wafa-deep hover:-translate-y-0.5 ${className}`}
    >
      {children}
    </Link>
  );
}

export function OutlineButton({ children, to = "/meyar", className = "", tone = "light", testid }) {
  const styles = tone === "dark"
    ? "border-white/40 text-white hover:bg-white/10"
    : "border-wafa-deep/25 text-wafa-deep hover:bg-wafa-cream/50";
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`inline-flex items-center justify-center gap-2 rounded-md border px-5 py-3 text-sm font-semibold transition-all hover:-translate-y-0.5 ${styles} ${className}`}
    >
      {children}
    </Link>
  );
}

/**
 * Circular process diagram — four nodes rotated round a labelled hub.
 * Configurable so both product pages can reuse it with different labels.
 */
export function CircularProcessDiagram({
  nodes,
  centerLine1 = "ALWAYS",
  centerLine2 = "ASSURED",
  ariaLabel = "Meyar continuous loop",
  testid = "meyar-process-diagram",
}) {
  const size = 340;
  const cx = size / 2;
  const cy = size / 2;
  const radius = 118;

  return (
    <svg viewBox={`0 0 ${size} ${size}`} role="img" aria-label={ariaLabel} data-testid={testid} className="mx-auto h-auto w-full max-w-[380px]">
      <circle cx={cx} cy={cy} r={radius + 30} fill="none" stroke={ACCENT} strokeOpacity="0.15" strokeWidth="1" />
      <circle cx={cx} cy={cy} r={radius} fill="none" stroke={ACCENT} strokeOpacity="0.45" strokeWidth="1.5" strokeDasharray="4 6" />
      {[0, 90, 180, 270].map((deg) => {
        const t = (deg + 45) * (Math.PI / 180);
        const x = cx + radius * Math.cos(t);
        const y = cy + radius * Math.sin(t);
        return (
          <polygon key={deg} points={`${x - 5},${y - 4} ${x + 5},${y} ${x - 5},${y + 4}`} transform={`rotate(${deg + 45 + 90} ${x} ${y})`} fill={ACCENT} opacity="0.7" />
        );
      })}
      {nodes.map((label, i) => {
        const angle = (i * 90 - 90) * (Math.PI / 180);
        const x = cx + radius * Math.cos(angle);
        const y = cy + radius * Math.sin(angle);
        return (
          <g key={label}>
            <circle cx={x} cy={y} r="34" fill="white" stroke={ACCENT} strokeWidth="2" />
            <text x={x} y={y + 4} textAnchor="middle" fontSize="12" fontWeight="600" fill="#0E4F3A" fontFamily="Outfit, system-ui, sans-serif">{label}</text>
          </g>
        );
      })}
      <circle cx={cx} cy={cy} r="44" fill={ACCENT} />
      <text x={cx} y={cy - 2} textAnchor="middle" fontSize="11" fontWeight="700" fill="white" fontFamily="Outfit, system-ui, sans-serif">{centerLine1}</text>
      <text x={cx} y={cy + 12} textAnchor="middle" fontSize="11" fontWeight="700" fill="white" fontFamily="Outfit, system-ui, sans-serif">{centerLine2}</text>
    </svg>
  );
}

export function AssuranceRow({ item, index }) {
  return (
    <details data-testid={`assurance-row-${index}`} className="group border-b border-wafa-deep/10 py-5 [&_summary::-webkit-details-marker]:hidden">
      <summary className="flex cursor-pointer items-center justify-between gap-6">
        <span className="font-heading text-lg font-semibold text-wafa-deep sm:text-xl">{item.title}</span>
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border transition-transform group-open:rotate-180" style={{ borderColor: ACCENT, color: ACCENT }}>
          <ChevronDown className="h-4 w-4" />
        </span>
      </summary>
      <p className="mt-4 max-w-3xl text-base leading-relaxed text-wafa-ink/80 lg:text-lg">{item.body}</p>
    </details>
  );
}
